import { Star, Loader2, FlaskConical } from "lucide-react";
import {
  Home, Sparkles, Camera, GraduationCap, PartyPopper, HeartHandshake, Car, User, Wrench, Briefcase,
} from "lucide-react";

const ICONS = { Home, Sparkles, Camera, GraduationCap, PartyPopper, HeartHandshake, Car, User, Wrench, Briefcase };

export const CategoryIcon = ({ name, className = "h-5 w-5" }) => {
  const Icon = ICONS[name] || Sparkles;
  return <Icon className={className} strokeWidth={1.75} />;
};

export const DemoBadge = ({ className = "" }) => (
  <span
    data-testid="demo-badge"
    title="Fictional sample business for testing"
    className={`inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ${className}`}
  >
    <FlaskConical className="h-3 w-3" /> Demo
  </span>
);

export const RatingStars = ({ value = 0, size = "h-4 w-4", testId }) => (
  <div className="flex items-center gap-0.5" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </div>
);

export const RatingPill = ({ rating, count }) => (
  <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900">
    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
    {rating ? Number(rating).toFixed(1) : "New"}
    <span className="font-normal text-slate-500">({count || 0})</span>
  </span>
);

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-sky-50 text-sky-700 border-sky-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  suspended: "bg-slate-100 text-slate-700 border-slate-300",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const StatusBadge = ({ status, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[status] || STATUS_STYLES.suspended}`}
  >
    {status}
  </span>
);

export const PageLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center" data-testid="page-loader">
    <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
    {Icon && <Icon className="mx-auto h-10 w-10 text-slate-300" />}
    <p className="mt-3 font-heading text-lg font-semibold text-slate-900">{title}</p>
    {text && <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const SectionHeading = ({ eyebrow, title, text, className = "" }) => (
  <div className={`max-w-2xl ${className}`}>
    {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">{eyebrow}</p>}
    <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{title}</h2>
    {text && <p className="mt-3 text-base text-slate-600 md:text-lg">{text}</p>}
  </div>
);
